import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { guestPortalApi } from '../../lib/api';
import { useGuestAuth } from '../../lib/guest-auth-context';
import { format } from 'date-fns';
import {
  Hotel,
  Calendar,
  MapPin,
  ArrowLeft,
  Loader2,
  Users,
  DoorOpen,
  CreditCard,
  FileText,
  XCircle,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';

export default function GuestReservationDetailPage() {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: authLoading } = useGuestAuth();
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/portal/login');
      return;
    }
    loadReservation();
  }, [isAuthenticated, authLoading, reservationId]);

  const loadReservation = async () => {
    try {
      setLoading(true);
      const response = await guestPortalApi.getReservation(token, reservationId);
      setReservation(response.data);
    } catch (error) {
      console.error('Failed to load reservation:', error);
      if (error.response?.status === 404 || error.response?.status === 403) {
        toast.error('Reservation not found');
        navigate('/portal/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setCancelling(true);
      await guestPortalApi.cancelReservation(token, reservationId, cancelReason || 'Guest requested cancellation');
      toast.success('Reservation cancelled successfully');
      setShowCancelDialog(false);
      loadReservation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel reservation');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      CONFIRMED: { class: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
      GUARANTEED: { class: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
      CHECKED_IN: { class: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: DoorOpen },
      CHECKED_OUT: { class: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400', icon: Clock },
      CANCELLED: { class: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
      NO_SHOW: { class: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400', icon: XCircle },
    };
    const c = config[status] || { class: 'bg-gray-100 text-gray-800', icon: Clock };
    const Icon = c.icon;
    return (
      <Badge className={`${c.class} gap-1`}>
        <Icon className="h-3 w-3" />
        {status?.replace('_', ' ')}
      </Badge>
    );
  };

  const canCancel = reservation && ['CONFIRMED', 'GUARANTEED', 'TENTATIVE'].includes(reservation.status);

  if (authLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-8 w-48 mb-6" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <Hotel className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-lg font-medium">Reservation not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/portal/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const property = reservation.properties || {};
  const roomType = reservation.room_types || {};
  const room = reservation.rooms || {};

  return (
    <div className="container mx-auto px-4 py-8" data-testid="guest-reservation-detail">
      {/* Back Button */}
      <Button variant="ghost" className="mb-6" onClick={() => navigate('/portal/dashboard')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Dashboard
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-1">{property.name || 'Hotel'}</h1>
          {property.address_city && (
            <p className="text-muted-foreground flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {property.address_city}
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            Confirmation: <span className="font-mono font-medium">{reservation.confirmation_number}</span>
          </p>
        </div>
        <div className="text-right">
          {getStatusBadge(reservation.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stay Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Stay Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-sm text-muted-foreground">Check-in</p>
                  <p className="font-semibold">{format(new Date(reservation.arrival_date), 'MMM d, yyyy')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Check-out</p>
                  <p className="font-semibold">{format(new Date(reservation.departure_date), 'MMM d, yyyy')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Duration</p>
                  <p className="font-semibold">{reservation.nights} night(s)</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Guests</p>
                  <p className="font-semibold flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {reservation.adults} adult(s){reservation.children > 0 && `, ${reservation.children} child(ren)`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Room Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DoorOpen className="h-5 w-5" />
                Room Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Room Type</span>
                  <span className="font-medium">{roomType.name || 'Standard Room'}</span>
                </div>
                {room.room_number && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Room Number</span>
                    <span className="font-medium">{room.room_number}</span>
                  </div>
                )}
                {room.floor && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Floor</span>
                    <span className="font-medium">{room.floor}</span>
                  </div>
                )}
                {roomType.description && (
                  <p className="text-sm text-muted-foreground pt-2 border-t">{roomType.description}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pre-Check-In Status */}
          {reservation.pre_checkin && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Pre-Check-In
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span>Status: <Badge variant="outline">{reservation.pre_checkin.status}</Badge></span>
                  {reservation.pre_checkin.status === 'pending' && (
                    <Button size="sm" onClick={() => navigate(`/portal/reservations/${reservationId}/pre-checkin`)}>
                      Complete Pre-Check-In
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Price Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Price Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {reservation.rate_amount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Rate per night</span>
                    <span>${reservation.rate_amount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{reservation.nights} night(s)</span>
                  <span>${(reservation.total_rate || reservation.grand_total || 0)?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span>${(reservation.grand_total || reservation.total_rate || 0)?.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Hotel Contact */}
          <Card>
            <CardHeader>
              <CardTitle>Hotel Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {property.address_city && (
                <p className="text-sm flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  {property.address_city}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="space-y-2">
            {canCancel && (
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => setShowCancelDialog(true)}
                data-testid="cancel-reservation-btn"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancel Reservation
              </Button>
            )}
            {['CONFIRMED', 'GUARANTEED'].includes(reservation.status) && !reservation.pre_checkin && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate(`/portal/reservations/${reservationId}/pre-checkin`)}
              >
                <FileText className="h-4 w-4 mr-2" />
                Pre-Check-In
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Reservation</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your reservation at {property.name}?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason for cancellation (optional)</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Please let us know why you're cancelling..."
              rows={3}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Keep Reservation
            </Button>
            <Button variant="destructive" onClick={handleCancel} disabled={cancelling}>
              {cancelling && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Cancellation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
